import { FilterControllerContext, FilterControllerT } from '../filter-controller';
import { FilterData, FilterTag } from '../filter-data';

import * as moment_ from 'moment';

/**
 * Defines an application-defined date preset.
 */
export interface DatePreset {
  /**
   * Internal id of the preset.
   */
  id: string;

  /**
   * Label to display on the filter UI for the preset.
   */
  label: string;

  /**
   * Returns the start date for this preset.
   */
  getStartDate: () => moment_.Moment;

  /**
   * Returns the end date for this preset.
   */
  getEndDate: () => moment_.Moment;
}

/**
 * Defines application-provided parameters for a date filter.
 */
export interface DateFilterParams {
  /**
   * Label for end date (default: 'End Date').
   */
  endDateLabel?: string;

  /**
   * Date format.  Defaults to 'MM/DD/YYYY' (see Momentjs formatting).
   */
  format?: string;

  /**
   * Placeholder string.
   */
  placeholder?: string;

  /**
   * Text to be shown under the date pickers.
   */
  instructionalText?: string;

  /**
   * The minimum date that can be picked.
   */
  minDate?: moment_.Moment;

  /**
   * The maximum date that can be picked.
   */
  maxDate?: moment_.Moment;

  /**
   * Defines presets the user can choose from.
   */
  presets?: DatePreset[];

  /**
   * Label for start date (default: 'Start Date').
   */
  startDateLabel?: string;

  /**
   * If set to true, will show the date selection.
   */
  showDate?: boolean;

  /**
   * If set to true, will show the date range selection.
   */
  showDateRange?: boolean;

  /**
   * If set to true, when date range selected, start date is required.
   */
  startDateRequired?: boolean;

  /**
   * If set to true, when date rage selected, end date is required.
   */
  endDateRequired?: boolean;
}

/**
 * Defines user-configured data for a date filter.
 */
export class DateFilterData implements FilterData {
  /**
   * Id of the selected preset.
   */
  selectionId?: string;

  /**
   * Defines the start date.
   */
  startDate?: moment_.Moment;

  /**
   * Defines the end date.
   */
  endDate?: moment_.Moment;

  /** FilterData implementation */

  clear(): void {
    this.selectionId = undefined;
    this.startDate = undefined;
    this.endDate = undefined;
  }

  copyFrom(data: DateFilterData): void {
    this.selectionId = data?.selectionId;
    this.startDate = data?.startDate;
    this.endDate = data?.endDate;
  }

  getTags(filterParams: DateFilterParams): FilterTag[] {
    if (!this.hasFilter()) {
      return [];
    }

    let description = '';
    const format = filterParams.format || 'MM/DD/YYYY';

    switch (this.selectionId) {
      case '$date':
        description = this.startDate.format(format);
        break;
      case '$date-range':
        if (this.startDate && this.endDate) {
          description = this.startDate.format(format) + ' - ' + this.endDate.format(format);
        } else if (this.startDate) {
          description = '>= ' + this.startDate.format(format);
        } else if (this.endDate) {
          description = '<= ' + this.endDate.format(format);
        }
        break;
      default:
        {
          const preset = filterParams.presets.find(value => value.id === this.selectionId);
          if (preset) {
            description = preset.label;
          }
        }
        break;
    }

    return [
      {
        description: description,
        remove: () => {
          if (this.hasFilter()) {
            const newFilterData = new DateFilterData();
            newFilterData.copyFrom(this);
            newFilterData.selectionId = undefined;
            newFilterData.startDate = undefined;
            newFilterData.endDate = undefined;
            return newFilterData;
          } else {
            return this;
          }
        }
      }
    ];
  }

  hasFilter(): boolean {
    // Since we only need to know if there is a filter in place, we only need to check the selection id.
    // We don't care about the min/max values since this isn't a validation method.
    return !!this.selectionId;
  }
}

/**
 * Defines the filter handler for a date filter.
 * The HTML template for the date filter is defined in virtual-grid-header-dropdown.html.
 */
export class DateFilterController extends FilterControllerT<DateFilterData, DateFilterParams> {
  constructor(context: FilterControllerContext) {
    super(context, () => {
      return new DateFilterData();
    });

    this.errors = {};
    this.state = {};
  }

  getDropdownWidth(): number {
    if (this.sourceParams.showDateRange && this.state.dateRangeOnly) {
      return 320;
    }

    if (this.sourceParams.showDate && this.state.dateOnly) {
      return 290;
    }

    return this.sourceParams.showDateRange || this.sourceParams.showDate ? 350 : 260;
  }

  getTemplateContext(): any {
    const context = super.getTemplateContext();

    context.errors = this.errors;
    context.state = this.state;
    context.setSelection = (selectionId: string): void => {
      // This is important to check.  We call this method when the user clicks on the option div.  However
      // for the 'date' and 'date-range' options, we show a date picker.  Clicking inside of that or on the
      // icon will cause this method to fire again because the option div is a parent of the picker and also
      // receives the click event.  We don't want to clear out the data in that case.
      if (this.state.selectionId !== selectionId) {
        this.errors.input = false;
        this.errors.startInput = false;
        this.errors.endInput = false;

        this.state.selectionId = selectionId;

        this.focusDateInput();
      }
    };

    return context;
  }

  onActivate(isActive: boolean): void {
    if (isActive) {
      this.focusDateInput();

      // Make sure the selection is fully visible.
      this.ensureInView('selection-wrapper', this.state.selectionId + '-wrapper');
    } else {
      super.onActivate(isActive);
    }
  }

  canApply(): boolean {
    if (this.state.dateOnly) {
      return !this.errors.input;
    }
    if (this.state.dateRangeOnly) {
      return this.canApplyDateRange(true);
    }
    switch (this.state.selectionId) {
      case '$date':
        // There's an error if the error flag is set or the startDate is undefined.
        return !this.errors.input && !!this.state.value;
      case '$date-range':
        // There's an error if either error flag is set, or both startDate and endDate are undefined.
        return this.canApplyDateRange();
      default:
        return true;
    }
  }

  private canApplyDateRange(allowEmptyInputs = false): boolean {
    if (this.errors.startInput || this.errors.endInput) {
      return false;
    }

    if (!this.state.startValue && !this.state.endValue) {
      return allowEmptyInputs;
    }

    if (this.sourceParams.startDateRequired && !this.state.startValue) {
      return false;
    }

    if (this.sourceParams.endDateRequired && !this.state.endValue) {
      return false;
    }

    return true;
  }

  canClear(): boolean {
    if (this.state.dateOnly) {
      return !!this.state.value;
    }
    if (this.state.dateRangeOnly) {
      return !!this.state.startValue || !!this.state.endValue;
    }
    return this.state.selectionId !== '$all-dates';
  }

  onClear(): void {
    super.onClear();
    this.state.selectionId = '$all-dates';
    this.state.value = undefined;
    this.state.startValue = undefined;
    this.state.endValue = undefined;
  }

  protected evaluateLocalData(): boolean {
    const target = this.localData;
    const source = this.sourceData;

    // First determine the new min/max dates on the local data.

    if (this.state.dateOnly) {
      if (this.state.value) {
        this.state.selectionId = '$date';
      } else {
        this.state.selectionId = undefined;
      }
    } else if (this.state.dateRangeOnly) {
      if (this.state.startValue || this.state.endValue) {
        this.state.selectionId = '$date-range';
      } else {
        this.state.selectionId = undefined;
      }
    }

    switch (this.state.selectionId) {
      case '$date':
        target.selectionId = this.state.selectionId;
        // The 'apply' button is disabled if there is no value or there's an error, so there should
        // always be a value in the state in this case.  But just to be sure, have a check.
        if (this.state.value) {
          target.startDate = moment_(this.state.value);
          target.endDate = moment_(this.state.value);
        } else {
          target.startDate = undefined;
          target.endDate = undefined;
        }
        this.evalMinMax();
        break;
      case '$date-range':
        target.selectionId = this.state.selectionId;
        // The start or end date could be invalid if the user just chose one for an open ended range.
        // Passing 'undefined' to the moment constructor however will return an object with today's
        // date, which we don't want.
        if (this.state.startValue) {
          target.startDate = moment_(this.state.startValue);
        } else {
          target.startDate = undefined;
        }
        if (this.state.endValue) {
          target.endDate = moment_(this.state.endValue);
        } else {
          target.endDate = undefined;
        }
        this.evalMinMax();
        break;
      default:
        {
          const preset = this.sourceParams?.presets?.find(value => value.id === this.state.selectionId);
          if (preset) {
            target.selectionId = preset.id;
            target.startDate = preset.getStartDate();
            target.endDate = preset.getEndDate();
          } else {
            target.selectionId = undefined;
            target.startDate = undefined;
            target.endDate = undefined;
          }
        }
        break;
    }

    // Next compare the dates with the source data.

    if (target.startDate && source?.startDate) {
      // If both objects are defined, compare the actual object values.
      if (target.startDate.isSame(source.startDate) === false) {
        return true;
      }
    } else {
      // If one or both are undefined, then return there was a transition to/from undefined.
      if (target.startDate !== source?.startDate) {
        return true;
      }
    }

    if (target.endDate && source?.endDate) {
      // If both objects are defined, compare the actual object values.
      if (target.endDate.isSame(source.endDate) === false) {
        return true;
      }
    } else {
      // If one or both are undefined, then return there was a transition to/from undefined.
      if (target.endDate !== source?.endDate) {
        return true;
      }
    }

    return false;
  }

  protected onInit(): void {
    super.onInit();

    this.errors.input = false;
    this.errors.startInput = false;
    this.errors.endInput = false;

    switch (this.sourceData?.selectionId) {
      case '$date':
        this.state.selectionId = this.sourceData.selectionId;
        this.state.value = this.sourceData.startDate;
        this.state.startValue = undefined;
        this.state.endValue = undefined;
        break;
      case '$date-range':
        this.state.selectionId = this.sourceData.selectionId;
        this.state.value = undefined;
        this.state.startValue = this.sourceData.startDate;
        this.state.endValue = this.sourceData.endDate;
        break;
      default:
        this.state.selectionId = this.sourceData?.hasFilter() ? this.sourceData.selectionId : '$all-dates';
        this.state.value = undefined;
        this.state.startValue = undefined;
        this.state.endValue = undefined;
    }

    this.state.dateOnly = false;
    this.state.dateRangeOnly = false;
    if (this.sourceParams) {
      if (this.sourceParams.presets?.length) {
        // There are presets.
      } else {
        if (!this.sourceParams.showDate && this.sourceParams.showDateRange) {
          this.state.dateRangeOnly = true;
        } else if (this.sourceParams.showDate && !this.sourceParams.showDateRange) {
          this.state.dateOnly = true;
        }
      }
    }
  }

  private evalMinMax(): void {
    if (this.localData.startDate) {
      this.localData.startDate.hours(0).minutes(0).seconds(0).milliseconds(0);
    }
    if (this.localData.endDate) {
      this.localData.endDate.hours(23).minutes(59).seconds(59).milliseconds(999);
    }
  }

  focusDateInput(): void {
    let focusElementName: string;

    if (this.state.dateOnly || this.state.selectionId === '$date') {
      focusElementName = `datepicker-${this.columnId}-$date`;
    } else if (this.state.dateRangeOnly || this.state.selectionId === '$date-range') {
      focusElementName = `datepicker-start-${this.columnId}-$date-range`;
    }

    if (focusElementName) {
      setTimeout(() => {
        const elements = this.document.getElementsByName(focusElementName);
        if (elements.length) {
          const element: HTMLInputElement = elements.item(0) as HTMLInputElement;
          if (element) {
            element.select();
            element.focus();
          }
        }
      });
    }
  }

  private errors: {
    input?: boolean;
    startInput?: boolean;
    endInput?: boolean;
  };

  private state: {
    selectionId?: string;
    value?: moment_.Moment;
    startValue?: moment_.Moment;
    endValue?: moment_.Moment;
    dateOnly?: boolean;
    dateRangeOnly?: boolean;
  };
}
